import React, { forwardRef, InputHTMLAttributes, ReactNode, KeyboardEvent } from 'react';
import { TextField, InputAdornment, IconButton as MuiIconButton, Chip, Box } from '@mui/material';
import { buildClassName } from 'utils/build-class-name';

//#region Types
export interface IconInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'size' | 'color'> {
  /** Current input value */
  value: string;
  /** Handler for value changes */
  onValueChange: (value: string) => void;
  /** Handler for Enter key press */
  onSubmit?: () => void;
  /** Icon component to display on the left */
  leftIcon?: ReactNode;
  /** Icon component to display in the middle (clear button) */
  middleIcon?: ReactNode;
  /** Icon component to display on the right (search/action button) */
  rightIcon?: ReactNode;
  /** Handler for left icon click */
  onLeftIconClick?: () => void;
  /** Handler for middle icon click */
  onMiddleIconClick?: () => void;
  /** Handler for right icon click */
  onRightIconClick?: () => void;
  /** Visual variant */
  variant?: 'default' | 'bordered' | 'filled';
  /** Whether to show the middle icon (usually based on value presence) */
  showMiddleIcon?: boolean;
  /** Tag content to display (e.g., chosen area) */
  tag?: string;
  /** Handler for tag close/remove */
  onTagRemove?: () => void;
  /** Test ID for automated testing */
  testId?: string;
  /** Test ID for middle icon */
  testIdMiddleIcon?: string;
  /** Additional CSS class */
  className?: string;
}
//#endregion

/**
 * IconInput - A text input with configurable icon slots
 * 
 * Atomic Level: Molecule
 * 
 * Features:
 * - Left, middle, and right icon slots
 * - Tag display with remove capability
 * - Controlled input with onValueChange callback
 * - Enter key submission support
 * - Multiple visual variants
 * - Built on Material UI TextField for enhanced features
 * 
 * @example
 * ```tsx
 * <IconInput
 *   value={query}
 *   onValueChange={setQuery}
 *   rightIcon={<SearchIcon />}
 *   middleIcon={<CloseIcon />}
 *   showMiddleIcon={query.length > 0}
 *   onRightIconClick={handleSearch}
 *   onMiddleIconClick={() => setQuery('')}
 *   placeholder="Search..."
 * />
 * ```
 */
const IconInput = forwardRef<HTMLInputElement, IconInputProps>(({
  value,
  onValueChange,
  onSubmit,
  leftIcon,
  middleIcon,
  rightIcon,
  onLeftIconClick,
  onMiddleIconClick,
  onRightIconClick,
  variant = 'default',
  showMiddleIcon = false,
  tag,
  onTagRemove,
  testId,
  testIdMiddleIcon,
  className,
  disabled,
  ...inputProps
}, ref) => {
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && onSubmit) {
      event.preventDefault();
      onSubmit();
    }
    // Call any existing onKeyDown handler
    inputProps.onKeyDown?.(event);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onValueChange(event.target.value);
  };

  // Map custom variant to MUI variant
  const getMuiVariant = () => {
    if (variant === 'filled') return 'filled';
    return 'outlined'; // default and bordered both use outlined
  };

  // Build start adornment (left icon or tag)
  const startAdornment = (
    <>
      {tag ? (
        <InputAdornment position="start">
          <Chip
            label={tag}
            onDelete={onTagRemove}
            deleteIcon={middleIcon ? <span>{middleIcon}</span> : undefined}
            size="small"
            sx={{
              height: '70%',
              backgroundColor: 'var(--themes-colors-tag-background, action.selected)',
              fontWeight: 600,
            }}
          />
        </InputAdornment>
      ) : leftIcon ? (
        <InputAdornment position="start">
          <MuiIconButton
            onClick={onLeftIconClick}
            edge="start"
            size="small"
            sx={{ color: 'var(--themes-colors-text-muted, text.secondary)' }}
          >
            {leftIcon}
          </MuiIconButton>
        </InputAdornment>
      ) : null}
    </>
  );

  // Build end adornment (middle clear icon and/or right action icon)
  const endAdornment = (
    <>
      {middleIcon && showMiddleIcon && !tag && (
        <InputAdornment position="end">
          <MuiIconButton
            onClick={onMiddleIconClick}
            size="small"
            data-testid={testIdMiddleIcon}
            sx={{ 
              color: 'var(--themes-colors-text-muted, text.secondary)',
              '& svg': {
                width: '0.8em',
                height: '0.8em',
              },
            }}
          >
            {middleIcon}
          </MuiIconButton>
        </InputAdornment>
      )}
      {rightIcon && (
        <InputAdornment position="end">
          <MuiIconButton
            onClick={onRightIconClick}
            edge="end"
            sx={{
              height: '2em',
              borderRadius: 'var(--themes-icons-border-radius, 50%)',
              color: 'var(--themes-colors-text-muted, text.secondary)',
            }}
          >
            {rightIcon}
          </MuiIconButton>
        </InputAdornment>
      )}
    </>
  );

  return (
    <Box className={buildClassName(className)} sx={{ width: '100%', height: '100%' }}>
      <TextField
        inputRef={ref}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        disabled={disabled || !!tag}
        variant={getMuiVariant()}
        fullWidth
        autoComplete="off"
        InputProps={{
          startAdornment,
          endAdornment,
          inputProps: {
            'data-testid': testId,
            ...inputProps,
          },
        }}
        sx={{
          height: '45px',
          '& .MuiOutlinedInput-root': {
            height: '45px',
            borderRadius: 'var(--themes-icons-border-radius, 100px)',
            backgroundColor: 'var(--themes-colors-background, white)',
            fontSize: '0.975em',
            transition: 'border-color 0.2s ease',
            boxShadow: 'var(--themes-shadows-light, box-shadow: 0px 1px 8px rgba(0, 0, 0, 0.08))',
            '&.Mui-disabled': {
              backgroundColor: 'var(--themes-colors-disabled-background, action.disabledBackground)',
            },
            '&.Mui-focused': {
              '& fieldset': {
                outline: 'none',
                border: '1px solid var(--themes-colors-primary, black)',
              },
            },
          },
          '& .MuiFilledInput-root': {
            height: '100%',
            borderRadius: 'var(--themes-icons-border-radius, 100px)',
            backgroundColor: 'var(--themes-colors-tertiary-background, action.hover)',
            border: 'none',
            '&:before, &:after': {
              display: 'none',
            },
          },
          '& .MuiInputBase-input': {
            padding: '0 0 0 1px !important',
            height: '45px',
            lineHeight: '45px',
            fontFamily: 'var(--font-family-sans-serif, inherit)',
            '&::placeholder': {
              color: 'var(--themes-colors-text-muted, text.secondary)! important',
              opacity: 0.5,
              lineHeight: '45px',
            },
            border: 'none !important',
          },
        }}
        {...inputProps}
      />
    </Box>
  );
});

IconInput.displayName = 'IconInput';

export default IconInput;
